import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { BlurredShadowBlob } from "../../common_ui_elements/blurred_shadow_blob.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { DiagonalStripesOverlay } from "../../common_ui_elements/diagonal_stripes_overlay.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UIProgressBar } from "../../common_ui_elements/ui_progress_bar.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";

export class LoadingScreen extends UIElement
{
  private static readonly FILL_ANIM_MS = 400;

  private static readonly LOGO_START_SCALE = 0.1;
  private static readonly LOGO_PEAK_SCALE = 1.3;
  private static readonly LOGO_DIP_SCALE = 0.9;

  private static readonly LOGO_GROW_MS = 200;
  private static readonly LOGO_DIP_MS = 130;
  private static readonly LOGO_RECOVER_MS = 200;

  public static readonly ENTRANCE_DURATION_MS =
    LoadingScreen.LOGO_GROW_MS + LoadingScreen.LOGO_DIP_MS + LoadingScreen.LOGO_RECOVER_MS;

  private progress_bar!: UIProgressBar;
  private stripes!: DiagonalStripesOverlay;
  private percentage_text!: UITextElement;

  private bar_x = 0;
  private bar_y = 0;
  private bar_w = 0;
  private bar_h = 0;
  private fill_border = 0;
  private fill_corner_size = 0;

  private target_progress = 0;
  private last_shown_percentage = -1;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );

    this.createBackground();
    this.createLogo();
    this.createProgressBar();
    this.createPercentageText();
  }

  private createBackground(): void
  {
    const screen_w = this.rect.getW();
    const screen_h = this.rect.getH();

    const image = ImagesHolder.getImage( ImgID.UI_LoadingBg );
    const scale = Math.max( screen_w / image.width, screen_h / image.height );

    const bg_w = image.width * scale;
    const bg_h = image.height * scale;
    const bg_x = ( screen_w - bg_w ) / 2;
    const bg_y = ( screen_h - bg_h ) / 2;

    const bg = new UIImage(
      this.ctx, bg_x, bg_y, bg_w, bg_h, ImgID.UI_LoadingBg
    );
    bg.setClickable( false );
    this.addChild( bg );
  }

  private static containImageInRect( img_id: ImgID, x: number, y: number, w: number, h: number )
  {
    const image = ImagesHolder.getImage( img_id );
    const native_ratio = image.width / image.height;
    const box_ratio = w / h;

    let fit_w = w;
    let fit_h = h;

    if ( native_ratio > box_ratio )
      fit_h = w / native_ratio;
    else
      fit_w = h * native_ratio;

    const cx = x + w / 2;
    const cy = y + h / 2;

    return { x: cx - fit_w / 2, y: cy - fit_h / 2, w: fit_w, h: fit_h };
  }

  private static readonly SUBSTRATE_W = 706;
  private static readonly SUBSTRATE_H = 438;
  private static readonly SUBSTRATE_Y = 173;
  private static readonly GROUP_Y = 47;

  private createLogo(): void
  {
    const logo_w = LayoutScaler.widthFromDesign( 866 );
    const logo_h = LayoutScaler.heightFromDesign( 428 );

    const logo_x = this.rect.getW2() - logo_w / 2;
    const logo_y = LayoutScaler.yFromDesign( 100 );

    const logo = new UIImage(
      this.ctx, logo_x, logo_y, logo_w, logo_h, ImgID.UI_LoadingLogo
    );

    this.addChild( logo );

    this.animateLogo( logo, performance.now() );
  }

  private animateLogo( logo: UIImage, now: number ): void
  {
    const start = new Vector2( LoadingScreen.LOGO_START_SCALE, LoadingScreen.LOGO_START_SCALE );
    const peak = new Vector2( LoadingScreen.LOGO_PEAK_SCALE, LoadingScreen.LOGO_PEAK_SCALE );
    const dip = new Vector2( LoadingScreen.LOGO_DIP_SCALE, LoadingScreen.LOGO_DIP_SCALE );
    const rest = new Vector2( 1, 1 );

    const t_peak = now + LoadingScreen.LOGO_GROW_MS;
    const t_dip = t_peak + LoadingScreen.LOGO_DIP_MS;

    logo.setScale( start );
    logo.addTransform( new TransformScale(
      logo, true, start, peak,
      now, LoadingScreen.LOGO_GROW_MS, { easing: Easing.EaseOutQuad }
    ) );
    logo.addTransform( new TransformScale(
      logo, true, peak, dip,
      t_peak, LoadingScreen.LOGO_DIP_MS, { easing: Easing.EaseInOutQuad }
    ) );
    logo.addTransform( new TransformScale(
      logo, true, dip, rest,
      t_dip, LoadingScreen.LOGO_RECOVER_MS, { easing: Easing.EaseOutQuad }
    ) );
  }

  private createProgressBar(): void
  {
    const content_x = this.rect.getW() / 2 - LayoutScaler.widthFromDesign( 1080 ) / 2;

    this.bar_x = content_x + LayoutScaler.xFromDesign( 286 );
    this.bar_y = LayoutScaler.yFromLayouts( 1608, 2019 );
    this.bar_w = LayoutScaler.widthFromDesign( 508 );
    this.bar_h = LayoutScaler.heightFromDesign( 58 );

    this.fill_border = LayoutScaler.widthFromDesign( 5 );
    this.fill_corner_size = LayoutScaler.widthFromDesign( 19 );

    this.progress_bar = new UIProgressBar(
      this.ctx, this.bar_x, this.bar_y, this.bar_w, this.bar_h,
      {
        base_img_id: ImgID.UI_LoadingProgressBarBase,
        fill_img_id: ImgID.UI_LoadingProgressBarFill,
        base_nine_slice: "loading_bar_base",
        fill_nine_slice: "loading_bar_fill",
        border_width: this.fill_border,
        base_corner_size: LayoutScaler.widthFromDesign( 28 ),
        fill_corner_size: this.fill_corner_size,
        initial_progress: 0,
      }
    );
    this.progress_bar.setClickable( false );
    this.addChild( this.progress_bar );
  }

  private createPercentageText(): void
  {
    const text_w = LayoutScaler.widthFromDesign( 220 );
    const text_h = LayoutScaler.heightFromDesign( 70 );
    const center_x = this.bar_x + this.bar_w / 2;
    const center_y = this.bar_y + this.bar_h + LayoutScaler.heightFromDesign( 46 );

    this.percentage_text = new UITextElement(
      this.ctx,
      center_x - text_w / 2,
      center_y - text_h / 2,
      text_w,
      text_h,
      {
        text: "0%",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 800,
        text_color: ColorScheme.black,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        outline_color: ColorScheme.white,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    );
    this.percentage_text.setClickable( false );
    this.addChild( this.percentage_text );
  }

  public setProgress( t: number ): void
  {
    const clamped = Math.max( 0, Math.min( 1, t ) );
    if ( Math.abs( clamped - this.target_progress ) < 0.001 )
      return;

    this.target_progress = clamped;
    this.progress_bar.animateToProgress( clamped, LoadingScreen.FILL_ANIM_MS );
    this.setShouldBeRendered();
  }

  private updatePercentageText(): void
  {
    const shown_percentage = Math.round( this.progress_bar.getProgress() * 100 );
    if ( shown_percentage === this.last_shown_percentage )
      return;

    this.last_shown_percentage = shown_percentage;
    this.percentage_text.setText( `${shown_percentage}%` );
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
    this.updatePercentageText();
  }

  public name(): string
  {
    return "LoadingScreen";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
